import React, { PropsWithChildren, useEffect } from "react";
import AnimatedPageTransition from "../../../../../domain/module/navigation/animated-page-transition";
import { useLanguageBasedFont } from "../../../../templates/monotone/const/font-utils";
import styled, { keyframes } from "styled-components";
import { MonotoneContainer } from "../../../../templates/monotone/components/monotone-container";
import { useChanelLandingPage } from "./use-chanel-landing-page";
import { twMerge } from "tailwind-merge";

const disableMeet = import.meta.env.VITE_DISABLE_MEET === "true";

export const ChanelLandingPage = () => {
  const fontClass = useLanguageBasedFont();

  const { gpsStatus, validationStatus, error, hasButtons, handleButtonClick } =
    useChanelLandingPage();

  // index.htmlから移動したスクリプトをページ表示時に実行
  useEffect(() => {
    if (disableMeet) {
      return;
    }

    // window.hrefStringを設定
    (window as any).hrefString = window.location.href;

    // 外部スクリプトを動的に読み込み
    const script = document.createElement("script");
    script.src =
      "https://strage.meet-meet.com/static/script/1.3/shareblock2_min.js?errorurl=https://lagaleriedu19mtokyo.ag-webguide.com/invalid&showwarning=false";
    document.head.appendChild(script);

    return () => {
      // コンポーネントがアンマウントされる際に、スクリプトタグを削除
      // const existingScript = document.querySelector(`script[src="${script.src}"]`);
      // if (existingScript) {
      //   existingScript.remove();
      // }
    };
  }, []);

  // メインコンテンツの表示制御
  const renderMainContent = () => {
    // GPS拒否時
    if (gpsStatus === "denied") {
      return (
        <MonotoneContainer className="px-[40px] relative items-center justify-center gap-[40px] p-8 text-center">
          <div className="w-full text-left space-y-4">
            <div>
              位置情報の使用が拒否されました。設定から位置情報の使用を許可してください。
            </div>
            <div>
              Location access was denied. Please enable location access.
            </div>
          </div>
        </MonotoneContainer>
      );
    }

    // 位置制限時
    if (validationStatus === "failed") {
      return (
        <MonotoneContainer className="px-[40px] relative items-center justify-center gap-[40px] p-8 text-center">
          <div className="w-full text-left space-y-4">
            <div>
              現在の位置ではこのサービスをご利用いただけません。指定された場所でアクセスしてください。
            </div>
            <div>
              This service is not available at your current location. Please
              access from the designated area.
            </div>
          </div>
        </MonotoneContainer>
      );
    }

    // 通常のランディングページ
    return (
      <MonotoneContainer>
        <div
          onClick={() => {
            if (!hasButtons) handleButtonClick("line");
          }}
          className={"flex flex-col h-full"}
        >
          <img
            className={
              "[@media(min-height:200px)]:w-[85%] [@media(min-height:500px)]:w-[90%] [@media(min-height:600px)]:w-full flex-shrink-0 mx-auto"
            }
            src={"/chanel/chanel-top.png"}
            alt={"top"}
          />

          {/* Audio Guide Section */}
          <div
            className={
              "flex-grow flex flex-col justify-center items-center gap-4"
            }
          >
            <div
              className={twMerge(
                !hasButtons &&
                  "border-[1px] border-black rounded-full w-fit mx-auto [@media(min-height:200px)]:px-6 py-2 [@media(min-height:600px)]:px-10 [@media(min-height:600px)]py-4"
              )}
            >
              <h4 className="[@media(min-height:400px)]:text-2xl [@media(min-height:500px)]:text-3xl [@media(min-height:700px)]:text-4xl font-serif text-gray-700">
                Audio Guide
              </h4>
            </div>
            {hasButtons && (
              <div
                className={`w-full flex justify-around items-center text-xs font-semibold ${fontClass}`}
              >
                <LinkButton
                  onClick={() => handleButtonClick("line")}
                  buttonType={"line"}
                >
                  <div>
                    LINE を持っている方
                    <br />
                    はこちら
                  </div>
                  <div>For LINE Users</div>
                </LinkButton>
                <LinkButton
                  onClick={() => handleButtonClick("non-line")}
                  buttonType={"non-line"}
                >
                  <div>
                    LINE を持っていない方 /<br />
                    海外からの方はこちら
                  </div>
                  <div>
                    For Non-LINE or
                    <br />
                    International Users
                  </div>
                </LinkButton>
              </div>
            )}
          </div>
        </div>
      </MonotoneContainer>
    );
  };

  const LinkButton = ({
    children,
    onClick,
    buttonType,
  }: PropsWithChildren<{
    onClick: () => void;
    buttonType: "line" | "non-line";
  }>) => {
    return (
      <div
        onClick={onClick}
        className={twMerge(
          "w-[45%] [@media(min-height:300px)]:h-[52px] [@media(min-height:500px)]:h-[64px] [@media(min-height:700px)]:h-[82px] [@media(min-height:200px)]:text-[6px] [@media(min-height:500px)]:text-[8px] [@media(min-height:700px)]:text-[10px] flex flex-col items-center justify-center [@media(min-height:300px)]:gap-[0] [@media(min-height:700px)]:gap-2 rounded-full [@media(min-height:300px)]:py-1 [@media(min-height:700px)]:py-4 cursor-pointer",
          buttonType === "line"
            ? "shadow-[0_0_20px_rgba(215,179,157,1.0)]"
            : "shadow-[0_0_20px_rgba(121,144,188,1.0)]"
        )}
      >
        {children}
      </div>
    );
  };

  // ローディング表示
  const renderLoadingOverlay = () => {
    if (gpsStatus === "requesting" || validationStatus === "validating") {
      return (
        <div className="fixed inset-0 z-40 bg-gray-100 bg-opacity-50 flex items-center justify-center">
          {/*<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>*/}
          <Spinner />
        </div>
      );
    }
    return null;
  };

  return (
    <AnimatedPageTransition>
      <div
        className={`bg-white flex items-center justify-center ${fontClass}`}
        style={{ height: window.innerHeight }}
      >
        {/* メインコンテンツ（状態によって切り替え） */}
        {renderMainContent()}

        {/* ローディングオーバーレイ（ローディング時のみ） */}
        {renderLoadingOverlay()}

        {/* エラーメッセージ */}
        {error && (
          <div className="fixed top-4 left-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
            {error}
          </div>
        )}
      </div>
    </AnimatedPageTransition>
  );
};

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #000000;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;
